import { useId, useState } from "react";
const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

/** UI review only. Tomy will connect company settings and server-authoritative pricing. */
export function CashCheckPricingSettings() {
  const id = useId();
  const [enabled, setEnabled] = useState(false);
  const [percent, setPercent] = useState("3.5");
  const rate = Number(percent);
  const validRate = percent.trim() !== "" && Number.isFinite(rate) && rate >= 0 && rate <= 100;
  const baseCents = 100000;
  const standardCents = enabled && validRate ? baseCents + Math.round(baseCents * rate / 100) : baseCents;
  const fieldClass = "mt-1.5 min-h-11 w-full rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-sm disabled:opacity-60";

  return <div className="max-w-2xl space-y-6">
    <p id={`${id}-notice`} role="status" className="rounded-lg bg-[var(--wash)] p-4 text-sm leading-6 text-[var(--ink-soft)]">
      Preview only. These controls do not save settings or change invoice prices yet.
    </p>
    <p className="text-sm leading-6 text-[var(--ink-soft)]">Set one rate for your business. Enabling this applies standard pricing across all pricebook items, with no item-by-item setup.</p>
    <label className="flex min-h-11 items-center gap-3 text-sm font-semibold">
      <input type="checkbox" checked={enabled} onChange={event => setEnabled(event.target.checked)} aria-describedby={`${id}-notice`} />
      Enable cash/check pricing for all items
    </label>
    <div className="max-w-sm">
      <label className="block text-sm font-semibold">
        Standard-price increase (%)
        <input type="number" min="0" max="100" step="0.01" value={percent} disabled={!enabled} onChange={event => setPercent(event.target.value)} aria-invalid={!validRate} aria-describedby={`${id}-rate-help`} className={fieldClass} />
        <span id={`${id}-rate-help`} className="mt-2 block text-xs font-normal leading-5 text-[var(--ink-soft)]">One rate for your entire pricebook, calculated before sales tax.</span>
      </label>
    </div>
    {!validRate && <p role="alert" className="text-sm text-[var(--accent)]">Enter a percentage between 0 and 100.</p>}
    <section aria-label="Pricing example" className="rounded-xl border border-[var(--line)] p-4">
      <h3 className="text-sm font-semibold">Example on a $1,000 job</h3>
      <p className="mt-1 text-xs text-[var(--ink-soft)]">Illustration before sales tax. This is not a saved invoice.</p>
      <dl className="mt-4 grid gap-4 sm:grid-cols-2">
        <div><dt className="text-sm text-[var(--ink-soft)]">Standard price / all cards</dt><dd className="mt-1 text-xl font-semibold">{validRate ? money.format(standardCents / 100) : "—"}</dd></div>
        <div><dt className="text-sm text-[var(--ink-soft)]">Cash or check</dt><dd className="mt-1 text-xl font-semibold">{money.format(baseCents / 100)}</dd></div>
      </dl>
    </section>
    <p className="text-sm leading-6 text-[var(--ink-soft)]">Cash or check removes the included increase only for the portion paid that way. Card payments use the standard price. New settings apply to new invoices and estimates; existing documents keep their pricing.</p>
    <div className="flex flex-wrap items-center gap-3">
      <button type="button" disabled aria-describedby={`${id}-notice`} className="min-h-11 rounded-lg bg-[var(--ink)] px-4 py-2 text-sm font-semibold text-[var(--paper)] opacity-50">Save pricing settings</button>
      <span className="text-xs text-[var(--ink-soft)]">Saving is not available yet.</span>
    </div>
  </div>;
}
